import { Formik, FormikValues } from 'formik';
import React, { useState, useEffect, useContext } from 'react';
import { Components } from './styled';
import useModalState from '../../../hooks/useModalState';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { Deal, DEAL_TYPES, SUBSCRIPTION_TYPE } from '../../../api/types/others';
import { getPastMidnightUTC } from '../../../utils/dateFormat';
import ProjectCalendarPicker from '../../dateAndTime/ProjectCalendarPicker';
import useSearchDropdown, {
  getSearchDropdownItems,
  getTextFromSelectedItems,
  SearchDropdownItem,
} from '../../../hooks/useSearchDropdown';
import ProjectSearchDropdown from '../../ProjectSearchDropdown';
import { getDisplayNames } from '../../../api/utils';
import { AppSettingsContext } from '../../../providers/AppSettingsProvider/context';

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
}: {
  modalState?: 'gift';
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const { collectionContentGroups } = useContext(AppSettingsContext);
  const collectionContentGroupIds = collectionContentGroups.map((t) => t.id);

  const [endDate, setEndDate] = useState<Date | null>(null);
  const [displayEndDate, setDisplayEndDate] = useState<Date | null>(null);

  const [formErrors, setFormErrors] = useState<{ type: string | null; context: string | null; endDate: string | null }>(
    {
      type: null,
      context: null,
      endDate: null,
    },
  );

  const typeDropdownProps = useSearchDropdown({
    items: getSearchDropdownItems<string>(DEAL_TYPES, (item) => getDisplayNames.dealType(item) || ''),
    dropdownType: 'basic',
    singleSelect: true,
  });

  const idDropdownProps = useSearchDropdown({
    items: getSearchDropdownItems<string>(
      ['monthly_subscription', 'yearly_subscription'].concat(collectionContentGroupIds),
      (item) => {
        if (item === 'monthly_subscription') {
          return 'Monthly subscription';
        } else if (item === 'yearly_subscription') {
          return 'Yearly subscription';
        }
        const foundCollection = collectionContentGroups.find((t) => t.id === item);
        if (foundCollection) {
          return foundCollection.title;
        }
        return item;
      },
    ),
    dropdownType: 'basic',
    singleSelect: true,
  });

  useEffect(() => {
    idDropdownProps.setSelectedItems([]);
  }, [typeDropdownProps.selectedItems]);

  const onSelectEndDate = (value: Date | null) => {
    if (value !== null) {
      setDisplayEndDate(value);
      const utcDate = new Date(getPastMidnightUTC(value) + 24 * 60 * 60 * 1000);
      setEndDate(utcDate);
    }
  };

  const onClearDate = () => {
    setDisplayEndDate(null);
    setEndDate(null);
  };

  const validateCustomFields = async (): Promise<boolean> => {
    let typeError = null;
    let contextError = null;
    let endDateError = null;
    let formValid = true;
    if (modalState === 'gift' && typeDropdownProps?.selectedItems?.length === 0) {
      typeError = 'Type is required';
      formValid = false;
    }
    if (modalState === 'gift' && idDropdownProps?.selectedItems?.length === 0) {
      contextError = 'Context is required';
      formValid = false;
    }
    if (modalState === 'gift' && typeDropdownProps?.selectedItems?.[0]?.value === 'subscription' && endDate === null) {
      endDateError = 'End at is required';
      formValid = false;
    }
    setFormErrors({ type: typeError, context: contextError, endDate: endDateError });
    return formValid;
  };

  const _onSubmit = async (values: FormikValues) => {
    const formValid = await validateCustomFields();
    formValid &&
      (await onSave?.({
        ...values,
        ...(endDate && { endAt: endDate.getTime() }),
        ...(typeDropdownProps?.selectedItems?.length > 0 && {
          type: typeDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
        }),
        ...(modalState === 'gift' &&
          idDropdownProps?.selectedItems?.length > 0 && {
            context: idDropdownProps?.selectedItems?.map((item) => item.value)?.[0],
          }),
      }));
  };

  const initialValues = {
    description: '',
  };

  return (
    <Formik initialValues={initialValues} onSubmit={_onSubmit}>
      {({ values, handleChange }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'required'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Type</Components.FieldLabel>

                  <Components.Picker
                    id={'type'}
                    onClick={modalState === 'gift' ? (e) => typeDropdownProps.show(e) : undefined}>
                    {typeDropdownProps?.selectedItems && typeDropdownProps?.selectedItems?.length > 0 ? (
                      <Components.PickerLabel>
                        {getTextFromSelectedItems(typeDropdownProps?.selectedItems)}
                      </Components.PickerLabel>
                    ) : (
                      <Components.NotSetLabel>None</Components.NotSetLabel>
                    )}

                    {modalState === 'gift' && (
                      <Components.PickerIconWrapper>
                        <Components.PickerIcon />
                      </Components.PickerIconWrapper>
                    )}
                  </Components.Picker>
                  {formErrors.type && typeDropdownProps.selectedItems.length === 0 && (
                    <Components.ErrorMessage>{`${formErrors.type}`}</Components.ErrorMessage>
                  )}
                  <ProjectSearchDropdown
                    {...typeDropdownProps}
                    facetFilter={(item) => ['product', 'subscription'].includes(item.value)}
                  />
                </Components.FieldWrapper>

                <Components.FieldWrapper>
                  <Components.FieldLabel>Context</Components.FieldLabel>
                  {modalState === 'gift' ? (
                    <>
                      <Components.Picker
                        disableCursor={
                          !(typeDropdownProps?.selectedItems && typeDropdownProps?.selectedItems?.length > 0)
                        }
                        onClick={(e) => typeDropdownProps.selectedItems.length > 0 && idDropdownProps.show(e)}>
                        {idDropdownProps?.selectedItems && idDropdownProps?.selectedItems?.length > 0 ? (
                          <Components.PickerLabel>
                            {getTextFromSelectedItems(idDropdownProps?.selectedItems)}
                          </Components.PickerLabel>
                        ) : (
                          <Components.NotSetLabel>
                            {typeDropdownProps?.selectedItems && typeDropdownProps?.selectedItems?.length > 0
                              ? 'None'
                              : 'Select the gift type first'}
                          </Components.NotSetLabel>
                        )}
                        {typeDropdownProps?.selectedItems && typeDropdownProps?.selectedItems?.length > 0 && (
                          <Components.PickerIconWrapper>
                            <Components.PickerIcon />
                          </Components.PickerIconWrapper>
                        )}
                      </Components.Picker>
                      {typeDropdownProps?.selectedItems && typeDropdownProps?.selectedItems?.length > 0 && (
                        <ProjectSearchDropdown
                          {...idDropdownProps}
                          facetFilter={(item) =>
                            typeDropdownProps.selectedItems?.[0]?.value === 'product'
                              ? collectionContentGroupIds.includes(item.value)
                              : ['monthly_subscription', 'yearly_subscription'].includes(item.value)
                          }
                        />
                      )}
                      {formErrors.context && idDropdownProps.selectedItems.length === 0 && (
                        <Components.ErrorMessage>{`${formErrors.context}`}</Components.ErrorMessage>
                      )}
                    </>
                  ) : null}
                </Components.FieldWrapper>

                {modalState === 'gift' && typeDropdownProps.selectedItems?.[0]?.value === 'subscription' && (
                  <Components.LengthInputsWrapper>
                    <Components.FieldWrapper>
                      <Components.FieldLabel>End at</Components.FieldLabel>
                      <ProjectCalendarPicker
                        dateValue={displayEndDate}
                        onDateValueChange={onSelectEndDate}
                        valueDisplayFormat={'day'}
                      />
                      {formErrors.endDate && endDate === null && (
                        <Components.ErrorMessage>{`${formErrors.endDate}`}</Components.ErrorMessage>
                      )}
                    </Components.FieldWrapper>
                    {endDate && <Components.ClearButton onClick={onClearDate}>Clear end at</Components.ClearButton>}
                  </Components.LengthInputsWrapper>
                )}
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            {modalState === 'gift' && typeDropdownProps.selectedItems?.[0]?.value != 'subscription' && (
              <Components.FieldsWrapper>
                <Components.FieldStartAdornment variant={'additional'} />
                <Components.FieldsContent>
                  <Components.LengthInputsWrapper>
                    <Components.FieldWrapper>
                      <Components.FieldLabel>End at</Components.FieldLabel>
                      <ProjectCalendarPicker
                        dateValue={displayEndDate}
                        onDateValueChange={onSelectEndDate}
                        valueDisplayFormat={'day'}
                      />
                      {formErrors.endDate && endDate === null && (
                        <Components.ErrorMessage>{`${formErrors.endDate}`}</Components.ErrorMessage>
                      )}
                    </Components.FieldWrapper>
                    {endDate && <Components.ClearButton onClick={onClearDate}>Clear end at</Components.ClearButton>}
                  </Components.LengthInputsWrapper>
                </Components.FieldsContent>
              </Components.FieldsWrapper>
            )}
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const CreateDealModal = ({
  isModalOpen,
  onModalClose,
  modalState,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { dealType?: 'gift'; deals?: Deal[]; setDeals?: React.Dispatch<React.SetStateAction<Deal[]>> };
}) => {
  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const data: Deal = {
      type: values?.type?.length ? values?.type : null,
      createdAt: new Date().getTime(),
      context: values?.context?.length ? values?.context : null,
      endAt: values?.endAt ? values?.endAt : null,
    };

    if (Object.keys(data).length > 0) {
      const newDeals = [...(modalState?.deals || [])];
      newDeals.push(data);
      modalState?.setDeals?.(newDeals);
      onModalClose?.();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={<ModalBody modalState={modalState?.dealType} onSave={_onSave} onCancel={_onModalClose} />}
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={`Create new ${modalState?.dealType || 'deal'}`}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default CreateDealModal;
